import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from "react-native";
import { getDocs, collection, doc, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import * as Print from "expo-print";
import * as Sharing from "expo-sharing";
import { AuthContext } from "../../Context/AuthContext";
import { Ionicons } from "@expo/vector-icons";
import { useFocusEffect } from "@react-navigation/native";
import RazorpayCheckout from "react-native-razorpay";
import GlobalStyles from "../../components/StylenColor";
import * as FileSystem from "expo-file-system";
import { Asset } from "expo-asset";
import RNPickerSelect from "react-native-picker-select";
import { ImprovementPoint } from "../../components/ImprovementPoint";
import { ScoreTrendIcon } from "../../components/ArrowCom";
import { useDesktopDetect, DashboardLayout, DesktopTheme, getScoreColor } from "../../components/web";

// Desktop Employee Card Component
const DesktopEmployeeCard = ({ employee, isHovered, onHoverIn, onHoverOut }) => {
  return (
    <View
      style={[
        desktopStyles.employeeCard,
        isHovered && desktopStyles.employeeCardHover,
      ]}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
    >
      <View style={desktopStyles.employeeHeader}>
        <View style={desktopStyles.avatarCircle}>
          <Text style={desktopStyles.avatarText}>
            {employee.userName?.charAt(0)?.toUpperCase() || '?'}
          </Text>
        </View>
        <View style={desktopStyles.employeeInfo}>
          <Text style={desktopStyles.employeeName}>{employee.userName}</Text>
          <Text style={desktopStyles.employeeId}>ID: {employee.employeeIdNum}</Text>
        </View>
      </View>

      <View style={desktopStyles.topicsList}>
        {employee.topics.map((topic, index) => (
          <View key={index} style={desktopStyles.topicItem}>
            <Text style={desktopStyles.topicName}>{topic.topic}</Text>
            <View style={desktopStyles.scoresRow}>
              <View style={desktopStyles.scoreBox}>
                <Text style={desktopStyles.scoreLabel}>Current</Text>
                <Text style={[desktopStyles.scoreValue, { color: getScoreColor(topic.totalScore * 10) }]}>
                  {topic.totalScore}
                </Text>
              </View>
              <View style={desktopStyles.scoreBox}>
                <Text style={desktopStyles.scoreLabel}>Previous</Text>
                <Text style={desktopStyles.scoreValueMuted}>
                  {topic.previousScore ?? 'NA'}
                </Text>
              </View>
              <ScoreTrendIcon
                current={topic.totalScore}
                previous={topic.previousScore}
                isDesktopWeb={true}
              />
            </View>
            <Text style={desktopStyles.topicDate}>{topic.date}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

// Desktop View Component
const DesktopImprovementReportView = ({
  loading,
  filteredResponses,
  selectedQuarter,
  setSelectedQuarter,
  isGeneratingPDF,
  generatePDF,
  latestResponses,
}) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredQuarter, setHoveredQuarter] = useState(null);
  const [hoveredDownload, setHoveredDownload] = useState(false);

  // Calculate stats
  const totalEmployees = filteredResponses.reduce((sum, dept) => sum + dept.employees.length, 0);
  const totalTopics = filteredResponses.reduce((sum, dept) =>
    sum + dept.employees.reduce((empSum, emp) => empSum + emp.topics.length, 0), 0);

  return (
    <DashboardLayout currentRoute="ImprovementReport">
      <ScrollView style={desktopStyles.container} showsVerticalScrollIndicator={false}>
        {/* Header Section */}
        <View style={desktopStyles.headerSection}>
          <View style={desktopStyles.headerLeft}>
            <Text style={desktopStyles.pageTitle}>Improvement Report</Text>
            <Text style={desktopStyles.pageSubtitle}>
              Employee performance tracking and improvement insights
            </Text>
          </View>

          <View style={desktopStyles.headerActions}>
            {/* Quarter Selector */}
            <View style={desktopStyles.quarterSelector}>
              {["Q1", "Q2", "Q3", "Q4"].map((q) => (
                <TouchableOpacity
                  key={q}
                  style={[
                    desktopStyles.quarterBtn,
                    selectedQuarter === q && desktopStyles.quarterBtnActive,
                    hoveredQuarter === q && selectedQuarter !== q && desktopStyles.quarterBtnHover,
                  ]}
                  onPress={() => setSelectedQuarter(q)}
                  onMouseEnter={() => setHoveredQuarter(q)}
                  onMouseLeave={() => setHoveredQuarter(null)}
                >
                  <Text
                    style={[
                      desktopStyles.quarterBtnText,
                      selectedQuarter === q && desktopStyles.quarterBtnTextActive,
                    ]}
                  >
                    {q}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Download Button */}
            <TouchableOpacity
              style={[
                desktopStyles.downloadBtn,
                hoveredDownload && desktopStyles.downloadBtnHover,
              ]}
              onPress={generatePDF}
              disabled={isGeneratingPDF}
              onMouseEnter={() => setHoveredDownload(true)}
              onMouseLeave={() => setHoveredDownload(false)}
            >
              {isGeneratingPDF ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Ionicons name="download-outline" size={20} color="#fff" />
                  <Text style={desktopStyles.downloadBtnText}>Download Report</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {/* Stats Row */}
        <View style={desktopStyles.statsRow}>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{filteredResponses.length}</Text>
            <Text style={desktopStyles.statLabel}>Departments</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{totalEmployees}</Text>
            <Text style={desktopStyles.statLabel}>Employees</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{totalTopics}</Text>
            <Text style={desktopStyles.statLabel}>Topic Responses</Text>
          </View>
          <View style={desktopStyles.statCard}>
            <Text style={desktopStyles.statValue}>{selectedQuarter}</Text>
            <Text style={desktopStyles.statLabel}>Selected Quarter</Text>
          </View>
        </View>

        {/* Content */}
        {loading ? (
          <View style={desktopStyles.loadingContainer}>
            <ActivityIndicator size="large" color={DesktopTheme.colors.primary} />
            <Text style={desktopStyles.loadingText}>Loading improvement data...</Text>
          </View>
        ) : filteredResponses.length === 0 ? (
          <View style={desktopStyles.emptyContainer}>
            <Text style={desktopStyles.emptyIcon}>📈</Text>
            <Text style={desktopStyles.emptyTitle}>No Data Available</Text>
            <Text style={desktopStyles.emptyText}>
              No improvement data found for {selectedQuarter}.
              Please ensure questionnaires have been submitted.
            </Text>
          </View>
        ) : (
          filteredResponses.map((dept, deptIndex) => (
            <View key={dept.department} style={desktopStyles.departmentSection}>
              <View style={desktopStyles.departmentHeader}>
                <View style={desktopStyles.departmentLine} />
                <Text style={desktopStyles.departmentTitle}>{dept.department}</Text>
                <View style={desktopStyles.departmentLine} />
              </View>

              <View style={desktopStyles.employeesGrid}>
                {dept.employees.map((emp, empIndex) => (
                  <DesktopEmployeeCard
                    key={emp.employeeIdNum}
                    employee={emp}
                    isHovered={hoveredCard === `${deptIndex}-${empIndex}`}
                    onHoverIn={() => setHoveredCard(`${deptIndex}-${empIndex}`)}
                    onHoverOut={() => setHoveredCard(null)}
                  />
                ))}
              </View>
            </View>
          ))
        )}
      </ScrollView>
    </DashboardLayout>
  );
};

// Desktop Styles
const desktopStyles = StyleSheet.create({
  container: {
    flex: 1,
    padding: DesktopTheme.spacing.lg,
    backgroundColor: DesktopTheme.colors.contentBg,
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.lg,
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.md,
  },
  headerLeft: {
    flex: 1,
    minWidth: 250,
  },
  pageTitle: {
    fontSize: DesktopTheme.typography.fontSize.h2,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.md,
  },
  quarterSelector: {
    flexDirection: 'row',
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: 4,
    ...DesktopTheme.shadows.sm,
  },
  quarterBtn: {
    paddingVertical: DesktopTheme.spacing.sm,
    paddingHorizontal: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
  },
  quarterBtnActive: {
    backgroundColor: DesktopTheme.colors.primary,
  },
  quarterBtnHover: {
    backgroundColor: DesktopTheme.colors.border,
  },
  quarterBtnText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '600',
    color: DesktopTheme.colors.textSecondary,
  },
  quarterBtnTextActive: {
    color: '#ffffff',
  },
  downloadBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.sm,
    backgroundColor: DesktopTheme.colors.primary,
    paddingVertical: DesktopTheme.spacing.sm + 2,
    paddingHorizontal: DesktopTheme.spacing.md,
    borderRadius: DesktopTheme.borderRadius.md,
    ...DesktopTheme.shadows.sm,
  },
  downloadBtnHover: {
    backgroundColor: DesktopTheme.colors.primaryHover,
  },
  downloadBtnText: {
    color: '#ffffff',
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    fontWeight: '600',
  },
  statsRow: {
    flexDirection: 'row',
    gap: DesktopTheme.spacing.md,
    marginBottom: DesktopTheme.spacing.lg,
  },
  statCard: {
    flex: 1,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.md,
    alignItems: 'center',
    ...DesktopTheme.shadows.sm,
  },
  statValue: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 100,
  },
  loadingText: {
    marginTop: DesktopTheme.spacing.md,
    fontSize: DesktopTheme.typography.fontSize.body,
    color: DesktopTheme.colors.textSecondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 80,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    ...DesktopTheme.shadows.sm,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: DesktopTheme.spacing.md,
  },
  emptyTitle: {
    fontSize: DesktopTheme.typography.fontSize.h3,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    marginBottom: DesktopTheme.spacing.sm,
  },
  emptyText: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textSecondary,
    textAlign: 'center',
    maxWidth: 400,
  },
  departmentSection: {
    marginBottom: DesktopTheme.spacing.xl,
  },
  departmentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.md,
  },
  departmentLine: {
    flex: 1,
    height: 2,
    backgroundColor: DesktopTheme.colors.border,
  },
  departmentTitle: {
    fontSize: DesktopTheme.typography.fontSize.h4,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    paddingHorizontal: DesktopTheme.spacing.md,
  },
  employeesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: DesktopTheme.spacing.md,
  },
  employeeCard: {
    width: 320,
    backgroundColor: DesktopTheme.colors.cardBg,
    borderRadius: DesktopTheme.borderRadius.lg,
    padding: DesktopTheme.spacing.md,
    ...DesktopTheme.shadows.card,
    transition: 'all 0.2s ease',
  },
  employeeCardHover: {
    transform: [{ translateY: -2 }],
    ...DesktopTheme.shadows.md,
  },
  employeeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: DesktopTheme.spacing.md,
    paddingBottom: DesktopTheme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: DesktopTheme.colors.border,
  },
  avatarCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: DesktopTheme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: DesktopTheme.spacing.sm,
  },
  avatarText: {
    color: '#ffffff',
    fontSize: DesktopTheme.typography.fontSize.bodyLarge,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontWeight: '700',
  },
  employeeInfo: {
    flex: 1,
  },
  employeeName: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '600',
  },
  employeeId: {
    fontSize: DesktopTheme.typography.fontSize.small,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  topicsList: {
    gap: DesktopTheme.spacing.sm,
  },
  topicItem: {
    backgroundColor: DesktopTheme.colors.contentBg,
    borderRadius: DesktopTheme.borderRadius.md,
    padding: DesktopTheme.spacing.sm,
  },
  topicName: {
    fontSize: DesktopTheme.typography.fontSize.body,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textPrimary,
    fontWeight: '500',
    marginBottom: DesktopTheme.spacing.xs,
  },
  scoresRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: DesktopTheme.spacing.md,
    marginBottom: DesktopTheme.spacing.xs,
  },
  scoreBox: {
    alignItems: 'center',
  },
  scoreLabel: {
    fontSize: DesktopTheme.typography.fontSize.tiny,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
  scoreValue: {
    fontSize: DesktopTheme.typography.fontSize.bodyLarge,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    fontWeight: '700',
  },
  scoreValueMuted: {
    fontSize: DesktopTheme.typography.fontSize.bodyLarge,
    fontFamily: DesktopTheme.typography.fontFamily.heading,
    color: DesktopTheme.colors.textMuted,
    fontWeight: '700',
  },
  topicDate: {
    fontSize: DesktopTheme.typography.fontSize.tiny,
    fontFamily: DesktopTheme.typography.fontFamily.body,
    color: DesktopTheme.colors.textMuted,
  },
});
const ImprovementReport = () => {
  const { companyId, companyName } = useContext(AuthContext);
  const [latestResponses, setLatestResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const { width } = useWindowDimensions();
  const isDesktopWeb = useDesktopDetect();
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [groupedData, setGroupedData] = useState({});
  const [logoBase64, setLogoBase64] = useState("");
  const getPreviousQuarter = (qtr) => {
    const quarters = ["Q1", "Q2", "Q3", "Q4"];
    const index = quarters.indexOf(qtr);
    return index > 0 ? quarters[index - 1] : null;
  };

  useEffect(() => {
    fetchLatestResponses(selectedQuarter);
  }, [selectedQuarter]);

  useFocusEffect(
    useCallback(() => {
      fetchLatestResponses(selectedQuarter);
    }, [selectedQuarter])
  );

  const showAlert = (title, message) => {
    if (Platform.OS === "web") {
      window.alert(`${title}\n\n${message}`);
    } else {
      showAlert(title, message);
    }
  };

  const fetchLatestResponses = async (quarter) => {
    try {
      setLoading(true);

      const responsesRef = collection(
        db,
        `companies/${companyId}/questionnaireResponses`
      );

      // Fetch current quarter responses
      const currentQ = query(responsesRef, where("quarter", "==", quarter));
      const currentSnap = await getDocs(currentQ);
      const currentResponses = currentSnap.docs.map((doc) => doc.data());

      // Fetch previous quarter responses
      const prevQuarter = getPreviousQuarter(quarter);
      let prevResponses = [];
      if (prevQuarter) {
        const prevQ = query(responsesRef, where("quarter", "==", prevQuarter));
        const prevSnap = await getDocs(prevQ);
        prevResponses = prevSnap.docs.map((doc) => doc.data());
      }

      // Map for quick access to previous scores
      const prevScoreMap = new Map();
      prevResponses.forEach((res) => {
        const key = `${res.employeeIdNum}-${res.topic}`;
        if (
          !prevScoreMap.has(key) ||
          res.timestamp.toDate() > prevScoreMap.get(key).timestamp.toDate()
        ) {
          prevScoreMap.set(key, res);
        }
      });

      // Map current quarter responses by employee-topic
      const latestMap = new Map();
      currentResponses.forEach((res) => {
        const key = `${res.employeeIdNum}-${res.topic}`;
        if (
          !latestMap.has(key) ||
          res.timestamp.toDate() > latestMap.get(key).timestamp.toDate()
        ) {
          latestMap.set(key, res);
        }
      });

      const latestWithPrev = [];
      const grouped = {};

      for (let res of Array.from(latestMap.values())) {
        const {
          department,
          employeeIdNum,
          userName,
          topic,
          totalScore,
          timestamp,
        } = res;

        const key = `${employeeIdNum}-${topic}`;
        const previousRes = prevScoreMap.get(key);
        const previousScore = previousRes?.totalScore ?? "NA";

        latestWithPrev.push({
          ...res,
          previousScore, // ✅ Now dynamically assigned
        });

        if (!grouped[department]) grouped[department] = {};

        if (!grouped[department][employeeIdNum]) {
          grouped[department][employeeIdNum] = {
            userName,
            employeeIdNum,
            topics: [],
          };
        }

        grouped[department][employeeIdNum].topics.push({
          topic,
          totalScore,
          previousScore,
          date: timestamp.toDate().toLocaleString(),
        });
      }

      setLatestResponses(latestWithPrev);
      setGroupedData(grouped);
    } catch (error) {
      console.error("Error fetching responses:", error);
      showAlert("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const filteredResponses = Object.entries(groupedData).map(
    ([department, employees]) => ({
      department,
      employees: Object.values(employees),
    })
  );

  useEffect(() => {
    const fetchLogoFromDB = async () => {
      try {
        const snapshot = await getDocs(collection(db, "sowin_image"));
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setLogoBase64(data.image); // full data URL
        }
      } catch (err) {
        console.error("Failed to fetch logo:", err);
      }
    };

    fetchLogoFromDB();
  }, []);

  const generatePDF = async () => {
    if (latestResponses.length === 0) {
      showAlert("No responses available to generate PDF");
      return;
    }

    try {
      setIsGeneratingPDF(true);

      const logoUrl = logoBase64 || "data:image/png;base64,..."; // replace with fallback if needed

      const coverPage = `
      <div style="page-break-after: always; padding: 40px; height: calc(100vh - 60px); box-sizing: border-box;">
        <div style="
          height: 100%;
          border: 4px solid #003366;
          border-radius: 12px;
          padding: 60px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        ">
          <img src="${logoUrl}" style="width: 220px;" />
          <h1 style="margin-top: 20px; color: #003366; font-size: 32px;">${companyName}</h1>
          <h2 style="color: #003366;font-size: 28px;">ESG Improvement Report</h2>
          <h3 style="color: #003366; font-size: 26px;">Quarter: ${selectedQuarter}</h3>
          <p style="margin-top: 100px; font-size: 22px;">Generated on: ${new Date().toLocaleString()}</p>
        </div>
      </div>
    `;
      const topicAnchors = []; // for index
      const responseDetailsHtml = await Promise.all(
        latestResponses.map(async (res, index) => {
          const anchorId = `topic-${index}`;
          topicAnchors.push({ anchorId, topic: res.topic });
          let updatedResponsesHtml = [];

          for (let i = 0; i < res.responses.length; i += 7) {
            const chunk = res.responses.slice(i, i + 7);
            const isFirstChunk = i === 0;
            const chunkPadding = isFirstChunk ? "" : "padding-top: 60px;";

            const chunkHtml = await Promise.all(
              chunk.map(async (resp, j) => {
                const questionIndex = i + j + 1;
                const improvementPoint = await ImprovementPoint(
                  res.department,
                  res.topic,
                  resp.selectedOption,
                  resp.question
                );

                const highlightColor =
                  improvementPoint === "No improvement needed"
                    ? "#d4edda"
                    : "#f8d7da";

                const improvementText =
                  improvementPoint === "No improvement needed"
                    ? "✔ Doing great! Keep it up!"
                    : improvementPoint;

                return `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">Q${questionIndex}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${resp.question}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${resp.selectedOption}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; background-color: ${highlightColor}; -webkit-print-color-adjust: exact !important;">${improvementText}</td>
                  </tr>
                `;
              })
            );

            updatedResponsesHtml.push(`
              <div style="page-break-after: always; ${chunkPadding}">
                <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
                  <thead>
                    <tr>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Q.No</th>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Question</th>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Selected Option</th>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #003366; color: white;">Improvement Point</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${chunkHtml.join("")}
                  </tbody>
                </table>
              </div>
            `);
          }

          const percentage = (
            (res.totalScore / (res.responses.length * 5)) *
            100
          ).toFixed(1);

          return `
            <div id="${anchorId}" style="page-break-before: always; padding: 30px; position: relative;">
              <div style="position: absolute; top: 20px; right: 20px; font-size: 14px; color: #555;">Page ${
                index + 1
              }</div>
              <h1 style="text-align: center; color: #003366;">Performance Report (${selectedQuarter})</h1>
              <h3 style="text-align: center; color: #003366; margin-top: 0;">${
                res.topic
              }</h3>
              <hr/>
              <p><b>Name:</b> ${res.userName}</p>
              <p><b>Employee ID:</b> ${res.employeeIdNum}</p>
              <p><b>Department:</b> ${res.department}</p>
              <p><b>Topic:</b> ${res.topic}</p>
              <p><b>Total Score:</b> ${res.totalScore} / ${
            res.responses.length * 5
          }</p>
              <p><b>Score Percentage:</b> ${percentage}%</p>
              <p><b>Date:</b> ${new Date(
                res.timestamp.toDate()
              ).toLocaleString()}</p>
              <div style="margin-top: 20px;">
                <h2 style="color: #003366;">Responses:</h2>
                ${updatedResponsesHtml.join("")}
              </div>
            </div>
          `;
        })
      );
      function chunkArray(array, size) {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
          chunks.push(array.slice(i, i + size));
        }
        return chunks;
      }

      const indexChunks = chunkArray(topicAnchors, 20);

      const indexPage = indexChunks
        .map((chunk, i) => {
          const className = i === 0 ? "index-page" : "index-cont-page";
          const showTitle =
            i === 0
              ? `<h1 style="text-align: center; color: #003366;">Index</h1>`
              : "";

          return `
            <div class="${className}">
              ${showTitle}
              <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
                <thead>
                  <tr>
                    <th style="border: 1px solid #ddd; padding: 10px; background-color: #003366; color: white; text-align: left;">
                      Sr. No.
                    </th>
                    <th style="border: 1px solid #ddd; padding: 10px; background-color: #003366; color: white; text-align: left;">
                      Topic Name
                    </th>
                  </tr>
                </thead>
                <tbody>
                  ${chunk
                    .map(
                      (item, idx) => `
                      <tr>
                        <td style="border: 1px solid #ddd; padding: 10px;">${
                          i * 20 + idx + 1
                        }</td>
                        <td style="border: 1px solid #ddd; padding: 10px;">
                          <a href="#${
                            item.anchorId
                          }" style="color: #003366; text-decoration: underline;">${
                        item.topic
                      }</a>
                        </td>
                      </tr>
                    `
                    )
                    .join("")}
                </tbody>
              </table>
            </div>
          `;
        })
        .join("");

      const footerHtml = `
        <footer style="position: fixed; bottom: 20px; left: 0; right: 0; text-align: center; font-size: 12px; color: #555;">
          Report for <b>${companyName}</b> – <b>${selectedQuarter}</b> generated on <b>${new Date().toLocaleString()}</b>
        </footer>
      `;

      const htmlContent = `
        <html>
          <head>
          <title>${companyName} ${selectedQuarter}- ESG Improvement Report</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 10px;
                margin-bottom: 60px;
                position: relative;
              }
              h1, h2 {
                color: #003366;
              }
              p {
                font-size: 14px;
                line-height: 1.5;
              }
              hr {
                border: 0;
                height: 1px;
                background: #ccc;
                margin: 10px 0;
              }
              table {
                font-size: 14px;
              }
              footer {
                position: fixed;
                bottom: 10px;
                left: 0;
                right: 0;
                text-align: center;
                font-size: 12px;
                color: #555;
              }
              .index-page {
                page-break-after: always;
                padding: 30px;
                padding-top: 0px; /* first index page */
              }
              .index-cont-page {
                page-break-after: always;
                padding: 30px;
                padding-top: 60px; /* only continuation pages */
              }
              @media print {
                @page {
                  margin-left: 0.2in;
                  margin-right: 0.2in;
                  margin-top: 0in;
                  margin-bottom: 0in;
                }
                body {
                  margin: 0 !important;
                  -webkit-print-color-adjust: exact !important;
                }
                thead th {
                  background-color: #003366 !important;
                  -webkit-print-color-adjust: exact;
                  color: white !important;
                }
                hr {
                  border: none;
                  height: 1px;
                  background-color: #003366 !important;
                  -webkit-print-color-adjust: exact;
                }
                .watermark {
                  color: rgba(0, 0, 0, 0.05) !important;
                  -webkit-print-color-adjust: exact;
                }
            </style>
          </head>
          <body>
          <!-- Watermark -->
          <div style="
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: 60px;
            font-family: Lato, sans-serif;
            font-weight: bold;
            color: rgba(0, 0, 0, 0.05);
            z-index: 0;
            white-space: nowrap;
            pointer-events: none;
          ">
          SOWIN ENTERPRISES
          </div>

          ${coverPage}
          ${indexPage}
          ${responseDetailsHtml.join("")}
          ${footerHtml}
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Popup blocked. Please allow popups for this site.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 700);
    } catch (error) {
      console.error("Print generation error:", error);
      showAlert("Please contact us", "Failed to generate report");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const sharePDF = async (uri) => {
    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(uri);
    } else {
      showAlert("Sharing not supported on this device");
    }
  };

  const handlePayment = async () => {
    const options = {
      description: "Download Improvement Report",
      image: "https://your-company-logo.com/logo.png",
      currency: "INR",
      key: "rzp_test_bpt69ThWsm9oqa",
      amount: 5000,
      name: "Sowin Power Ptv Ltd.",
      prefill: {
        email: "user@example.com",
        contact: "9999999999",
        name: "John Doe",
      },
      theme: { color: "#007c7c" },
    };

    try {
      const data = await RazorpayCheckout.open(options);
      if (data.razorpay_payment_id) {
        showAlert("Payment successful! Generating report...");
        generatePDF();
      }
    } catch (error) {
      showAlert("Payment failed. Please try again.");
      console.error("Payment Error:", error);
    }
  };

  // Desktop Web View with new theme
  if (isDesktopWeb) {
    return (
      <DesktopImprovementReportView
        loading={loading}
        filteredResponses={filteredResponses}
        selectedQuarter={selectedQuarter}
        setSelectedQuarter={setSelectedQuarter}
        isGeneratingPDF={isGeneratingPDF}
        generatePDF={generatePDF}
        latestResponses={latestResponses}
      />
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  // Mobile View
  return (
    <View style={styles.container}>
      {/* Desktop/Mobile Header Row */}
      <View style={[isDesktopWeb && styles.headerRowWeb]}>
        {/* Title on the left */}
        <Text
          style={[
            styles.title,
            GlobalStyles.titleTxtOffset,
            isDesktopWeb && styles.titleWeb,
          ]}
        >
          Improvement Report
        </Text>

        {/* Quarter + Download on right */}
        {isDesktopWeb && (
          <View style={styles.headerRightGroup}>
            <View>
              <Text style={styles.quarterLabel}>Select Quarter:</Text>
              <View style={styles.pickerContainer}>
                <RNPickerSelect
                  onValueChange={(value) => setSelectedQuarter(value)}
                  value={selectedQuarter}
                  useNativeAndroidPickerStyle={false}
                  placeholder={{}}
                  style={{
                    inputIOS: {
                      fontSize: GlobalStyles.fontSize.topTitle,
                      fontFamily: GlobalStyles.GlobalFont.fontFamily,
                      color: "#333",
                      paddingVertical: 10,
                    },
                    inputAndroid: {
                      fontSize: GlobalStyles.fontSize.topTitle,
                      fontFamily: GlobalStyles.GlobalFont.fontFamily,
                      color: "#333",
                    },
                  }}
                  items={[
                    { label: "Q1 (Jan-Mar)", value: "Q1" },
                    { label: "Q2 (Apr-Jun)", value: "Q2" },
                    { label: "Q3 (Jul-Sep)", value: "Q3" },
                    { label: "Q4 (Oct-Dec)", value: "Q4" },
                  ]}
                />
              </View>
            </View>

            <TouchableOpacity
              style={[styles.downloadButtonWeb, GlobalStyles.btnBgShadow]}
              onPress={generatePDF}
              disabled={isGeneratingPDF}
            >
              {isGeneratingPDF ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <>
                  <Ionicons name="download-outline" size={25} color="white" />
                  <Text
                    style={[
                      styles.buttonText,
                      GlobalStyles.btntxtoffset,
                      isDesktopWeb && styles.buttonTextWeb,
                    ]}
                  >
                    Download Improvement Report
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Quarter picker for mobile */}
      {!isDesktopWeb && (
        <>
          <Text style={styles.quarterLabel}>Select Quarter:</Text>
          <View style={styles.pickerContainer}>
            <RNPickerSelect
              onValueChange={(value) => setSelectedQuarter(value)}
              value={selectedQuarter}
              useNativeAndroidPickerStyle={false}
              placeholder={{}}
              style={{
                inputIOS: {
                  fontSize: GlobalStyles.fontSize.topTitle,
                  fontFamily: GlobalStyles.GlobalFont.fontFamily,
                  color: "#333",
                  paddingVertical: 10,
                },
                inputAndroid: {
                  fontSize: GlobalStyles.fontSize.topTitle,
                  fontFamily: GlobalStyles.GlobalFont.fontFamily,
                  color: "#333",
                },
              }}
              items={[
                { label: "Q1 (Jan-Mar)", value: "Q1" },
                { label: "Q2 (Apr-Jun)", value: "Q2" },
                { label: "Q3 (Jul-Sep)", value: "Q3" },
                { label: "Q4 (Oct-Dec)", value: "Q4" },
              ]}
            />
          </View>
        </>
      )}

      {/* List */}
      <FlatList
        data={filteredResponses}
        keyExtractor={(item) => item.department}
        renderItem={({ item }) => (
          <View style={{ marginBottom: 20 }}>
            {isDesktopWeb ? (
              <View style={styles.departmentWrapperWeb}>
                <View style={styles.hrLine} />
                <Text
                  style={[styles.departmentHeader, styles.departmentHeaderWeb]}
                >
                  {item.department}
                </Text>
                <View style={styles.hrLine} />
              </View>
            ) : (
              <Text style={styles.departmentHeader}>{item.department}</Text>
            )}

            <View style={isDesktopWeb && styles.cardGrid}>
              {item.employees.map((emp) => (
                <View
                  key={emp.employeeIdNum}
                  style={[
                    styles.responseCard,
                    GlobalStyles.btnBgShadow,
                    isDesktopWeb && styles.responseCardWeb,
                  ]}
                >
                  <Text
                    style={[
                      styles.employeeName,
                      isDesktopWeb && styles.employeeNameWeb,
                    ]}
                  >
                    {emp.userName} ({emp.employeeIdNum})
                  </Text>
                  {emp.topics.map((topic, index) => (
                    <View key={index} style={styles.topicContainer}>
                      <Text
                        style={[
                          styles.details,
                          isDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Topic: {topic.topic}
                      </Text>
                      <Text
                        style={[
                          styles.details,
                          isDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Latest Score: {topic.totalScore}
                      </Text>
                      <Text
                        style={[
                          styles.details,
                          isDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Previous Score: {topic?.previousScore ?? "NA"}
                      </Text>
                      <ScoreTrendIcon
                        current={topic.totalScore}
                        previous={topic.previousScore}
                        isDesktopWeb={isDesktopWeb}
                      />
                      <Text
                        style={[
                          styles.details,
                          isDesktopWeb && styles.detailsWeb,
                        ]}
                      >
                        Date: {topic.date}
                      </Text>
                      <View style={styles.topicDivider} />
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </View>
        )}
      />

      {/* Mobile Download Button */}
      {!isDesktopWeb && (
        <TouchableOpacity
          style={[styles.downloadButton, GlobalStyles.btnBgShadow]}
          onPress={generatePDF}
          disabled={isGeneratingPDF}
        >
          {isGeneratingPDF ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Ionicons name="download-outline" size={25} color="white" />
              <Text style={[styles.buttonText, GlobalStyles.btntxtoffset]}>
                Download Improvement Report
              </Text>
            </>
          )}
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 15,
    backgroundColor: GlobalStyles.GlobalBg.backgroundColor,
  },
  title: {
    color: GlobalStyles.GlobalBg.headerColor,
    fontSize: GlobalStyles.fontSize.xl,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    textAlign: "center",
    marginBottom: 10,
    paddingVertical: 1,
  },
  departmentHeader: {
    fontSize: GlobalStyles.fontSize.lg,
    marginVertical: 10,
    marginLeft: 5,
    color: GlobalStyles.GlobalBg.headerColor,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
  },
  headerRowWeb: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    alignSelf: "baseline",
  },
  titleWeb: {
    flex: 1,
    textAlign: "left",
    fontSize: GlobalStyles.fontSize.lg,
  },
  headerRightGroup: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 20,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
    backgroundColor: "#effdee",
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === "ios" ? 10 : 0,
  },
  quarterLabel: {
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    fontSize: GlobalStyles.fontSize.sm,
    color: "#333",
    marginBottom: 6,
  },
  downloadButtonWeb: {
    flexDirection: "row",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonTextWeb: {
    fontSize: GlobalStyles.fontSize.xs,
  },
  hrLine: {
    borderBottomWidth: 2,
    borderBottomColor: "#254128",
    marginVertical: 10,
  },
  departmentWrapperWeb: {
    marginTop: 10,
    marginBottom: 10,
  },
  responseCard: {
    backgroundColor: "#effdee",
    borderRadius: 10,
    padding: 15,
    marginBottom: 15,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  employeeName: {
    fontSize: GlobalStyles.fontSize.sm,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    marginBottom: 2,
    paddingVertical: 1,
  },
  details: {
    fontSize: GlobalStyles.fontSize.xss,
    color: "#555",
    marginBottom: 2,
    paddingVertical: 1,
    fontFamily: GlobalStyles.inpFont.fontFamily,
  },
  topicContainer: {
    marginTop: 10,
  },
  topicDivider: {
    height: 1,
    backgroundColor: "#ddd",
    marginTop: 10,
  },
  downloadButton: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: GlobalStyles.btnColor.backgroundColor,
    paddingVertical: 14,
    borderRadius: 10,
    marginVertical: 15,
  },
  buttonText: {
    color: "white",
    fontSize: GlobalStyles.fontSize.btn,
    marginLeft: 10,
    fontFamily: GlobalStyles.GlobalFont.fontFamily,
    fontWeight: "600",
  },
  headerRow: {
    flexDirection: "column",
    gap: 8,
  },
  headerRowWeb: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  headerControls: {
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 8,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: 10,
  },
  responseCardWeb: {
    width: "32%",
  },
  employeeNameWeb: {
    fontSize: GlobalStyles.fontSize.xss,
  },
  detailsWeb: {
    fontSize: GlobalStyles.fontSize.xsss - 1,
  },
  departmentHeaderWeb: {
    fontSize: GlobalStyles.fontSize.md,
    alignContent: "center",
    alignSelf: "center",
  },
});

export default ImprovementReport;
